/**
 * Centralized query key factory for React Query
 * Ensures consistent and type-safe query key management
 */

import type { InvestorFilters } from '@/types/investor';
import type { LedgerView } from '@/types/drawdown';
import type {
  FundTransactionCategory,
  FundTransactionStatus,
  FundTransactionType,
} from '@/types/fund-transactions';
import type { PendingTasksQuery } from '@/types/pendingTasks';
import type { FundInvestmentDirection } from '@/types/fundInvestment';

export const queryKeys = {
  // Auth
  auth: {
    all: ['auth'] as const,
    currentUser: ['auth', 'currentUser'] as const,
    status: ['auth', 'status'] as const,
    user: () => [...queryKeys.auth.all, 'user'] as const,
    session: () => [...queryKeys.auth.all, 'session'] as const,
  },

  // Agent (MCP) OAuth
  agentAuth: {
    all: ['agentAuth'] as const,
    consentContext: (params: Readonly<Record<string, unknown>>) =>
      [...queryKeys.agentAuth.all, 'consentContext', params] as const,
  },

  // Investors
  investors: {
    all: ['investors'] as const,
    lists: () => [...queryKeys.investors.all, 'list'] as const,
    list: (
      filters?: InvestorFilters,
      pagination?: { page?: number; page_size?: number }
    ) => [...queryKeys.investors.lists(), filters, pagination] as const,
    details: () => [...queryKeys.investors.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.investors.details(), id] as const,
    statistics: () => [...queryKeys.investors.all, 'statistics'] as const,
    fundInvestors: (fundId: string) =>
      [...queryKeys.investors.all, 'fund', fundId] as const,
    fundStatistics: (fundId: string) =>
      [...queryKeys.investors.all, 'fund', fundId, 'statistics'] as const,
    investmentOverview: (investorId: string) =>
      [...queryKeys.investors.all, investorId, 'overview'] as const,
    contacts: (investorId: string) =>
      [...queryKeys.investors.all, investorId, 'contacts'] as const,
    contact: (investorId: string, contactId: string) =>
      [...queryKeys.investors.all, investorId, 'contacts', contactId] as const,
    contactSearch: (search: string) =>
      [...queryKeys.investors.all, 'contacts', 'search', search] as const,
    emails: (investorId: string, params?: Record<string, unknown>) =>
      [...queryKeys.investors.all, investorId, 'emails', params] as const,
    emailDetail: (investorId: string, emailId: string) =>
      [...queryKeys.investors.all, investorId, 'emails', emailId] as const,
    exportLogs: (params: Record<string, unknown>) =>
      [...queryKeys.investors.all, 'export-logs', params] as const,
  },

  // Funds
  funds: {
    all: ['funds'] as const,
    lists: () => [...queryKeys.funds.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.funds.lists(), filters] as const,
    options: (params?: Record<string, unknown>) =>
      [...queryKeys.funds.all, 'options', params] as const,
    details: () => [...queryKeys.funds.all, 'detail'] as const,
    detail: (id: string) => ['funds', 'detail', id] as const,
    statistics: () => [...queryKeys.funds.all, 'statistics'] as const,
    investors: (id: string) => ['funds', 'detail', id, 'investors'] as const,
    documents: (fundId: string) =>
      [...queryKeys.funds.all, fundId, 'documents'] as const,
    bankAccounts: (id: string, activeOnly?: boolean) =>
      ['funds', 'detail', id, 'bank-accounts', activeOnly] as const,
    entities: (id: string) => ['funds', 'detail', id, 'entities'] as const,
    fundInvestments: (id: string, direction: FundInvestmentDirection) =>
      ['funds', 'detail', id, 'fund-investments', direction] as const,
    lpaTerms: (id: string) => ['funds', 'detail', id, 'lpa-terms'] as const,
    emails: (id: string, params?: Record<string, unknown>) =>
      ['funds', 'detail', id, 'emails', params] as const,
    emailDetail: (fundId: string, batchId: string) =>
      [...queryKeys.funds.all, fundId, 'emails', batchId] as const,
    distributionWaterfall: (
      fundId: string,
      type: 'realised' | 'unrealised' = 'realised'
    ) =>
      [...queryKeys.funds.all, fundId, 'distributionWaterfall', type] as const,
    convertibles: (id: string, outstandingOnly: boolean) =>
      ['funds', 'detail', id, 'convertibles', outstandingOnly] as const,
    portalPublication: (id: string) =>
      ['funds', 'detail', id, 'portal-publication'] as const,
    metricsReleaseQueue: () =>
      [...queryKeys.funds.all, 'metrics-release-queue'] as const,
  },

  // Portfolio
  portfolio: {
    all: ['portfolio'] as const,
    lists: () => [...queryKeys.portfolio.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.portfolio.lists(), filters] as const,
    details: () => [...queryKeys.portfolio.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.portfolio.details(), id] as const,
    summary: (fundId?: string, fundInvestments?: string) =>
      [...queryKeys.portfolio.all, 'summary', fundId, fundInvestments] as const,
    sectors: (fundId?: string, fundInvestments?: string) =>
      [...queryKeys.portfolio.all, 'sectors', fundId, fundInvestments] as const,
    contacts: (companyId: string) =>
      [...queryKeys.portfolio.all, companyId, 'contacts'] as const,
    bankAccounts: (companyId: string) =>
      [...queryKeys.portfolio.all, companyId, 'bank-accounts'] as const,
    deletionEligibility: (companyId: string) =>
      [...queryKeys.portfolio.all, companyId, 'deletion-eligibility'] as const,
    valuationsSummary: (fundId: string) =>
      [...queryKeys.portfolio.all, 'valuationsSummary', fundId] as const,
    financialMetrics: (companyId: string) =>
      [
        ...queryKeys.portfolio.all,
        companyId,
        'financialMetricsPaginated',
      ] as const,
    financialMetricsPaginated: (
      companyId: string,
      params?: Record<string, unknown>
    ) => [...queryKeys.portfolio.financialMetrics(companyId), params] as const,
  },

  // Capital Calls
  capitalCalls: {
    all: ['capitalCalls'] as const,
    lists: () => [...queryKeys.capitalCalls.all, 'list'] as const,
    list: (fundId?: string) =>
      [...queryKeys.capitalCalls.lists(), fundId] as const,
    details: () => [...queryKeys.capitalCalls.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.capitalCalls.details(), id] as const,
    calculations: (callId: string) =>
      [...queryKeys.capitalCalls.all, callId, 'calculations'] as const,
  },

  // Distributions
  distributions: {
    all: ['distributions'] as const,
    lists: () => [...queryKeys.distributions.all, 'list'] as const,
    list: (fundId?: string) =>
      [...queryKeys.distributions.lists(), fundId] as const,
    details: () => [...queryKeys.distributions.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.distributions.details(), id] as const,
    waterfall: (distributionId: string) =>
      [...queryKeys.distributions.all, distributionId, 'waterfall'] as const,
    availableAssetSales: (fundId: string) =>
      [
        ...queryKeys.distributions.all,
        fundId,
        'available-asset-sales',
      ] as const,
    notices: (distributionId: string) =>
      [...queryKeys.distributions.all, distributionId, 'notices'] as const,
  },

  // Documents
  documents: {
    all: ['documents'] as const,
    lists: () => [...queryKeys.documents.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.documents.lists(), filters] as const,
    detail: (id: string) => [...queryKeys.documents.all, 'detail', id] as const,
    folders: () => [...queryKeys.documents.all, 'folders'] as const,
    investorFundFolders: (investorId: string) =>
      [
        ...queryKeys.documents.all,
        'investor',
        investorId,
        'fund-folders',
      ] as const,
  },

  // Users & Roles
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.users.lists(), filters] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
    active: (search?: string) =>
      [...queryKeys.users.all, 'active', search] as const,
  },

  roles: {
    all: ['roles'] as const,
    lists: () => [...queryKeys.roles.all, 'list'] as const,
    list: () => [...queryKeys.roles.lists()] as const,
    permissions: () => [...queryKeys.roles.all, 'permissions'] as const,
  },

  // Change Requests
  changeRequests: {
    all: ['changeRequests'] as const,
    lists: () => [...queryKeys.changeRequests.all, 'list'] as const,
    pending: (investorId?: string) =>
      [...queryKeys.changeRequests.lists(), 'pending', investorId] as const,
    investor: (investorId: string) =>
      [...queryKeys.changeRequests.lists(), 'investor', investorId] as const,
    myRequests: () =>
      [...queryKeys.changeRequests.lists(), 'myRequests'] as const,
    details: () => [...queryKeys.changeRequests.all, 'detail'] as const,
    detail: (id: string) =>
      [...queryKeys.changeRequests.details(), id] as const,
  },

  // Scheduler
  scheduler: {
    all: ['scheduler'] as const,
    jobs: {
      all: () => [...queryKeys.scheduler.all, 'jobs'] as const,
      lists: () => [...queryKeys.scheduler.jobs.all(), 'list'] as const,
      list: (filters?: Record<string, unknown>) =>
        [...queryKeys.scheduler.jobs.lists(), filters] as const,
      details: () => [...queryKeys.scheduler.jobs.all(), 'detail'] as const,
      detail: (id: string) =>
        [...queryKeys.scheduler.jobs.details(), id] as const,
      types: () => [...queryKeys.scheduler.jobs.all(), 'types'] as const,
      statistics: () =>
        [...queryKeys.scheduler.jobs.all(), 'statistics'] as const,
      active: () => [...queryKeys.scheduler.jobs.all(), 'active'] as const,
    },
    history: {
      all: () => [...queryKeys.scheduler.all, 'history'] as const,
      lists: () => [...queryKeys.scheduler.history.all(), 'list'] as const,
      list: (filters?: Record<string, unknown>) =>
        [...queryKeys.scheduler.history.lists(), filters] as const,
      detail: (id: string) =>
        [...queryKeys.scheduler.history.all(), 'detail', id] as const,
    },
    config: {
      cleanup: () => [...queryKeys.scheduler.all, 'config', 'cleanup'] as const,
    },
  },

  // Metrics
  // Fund reports
  fundReports: {
    all: ['fundReports'] as const,
    lists: () => [...queryKeys.fundReports.all, 'list'] as const,
    list: (fundId: string) =>
      [...queryKeys.fundReports.lists(), fundId] as const,
    details: () => [...queryKeys.fundReports.all, 'detail'] as const,
    detail: (fundId: string, reportId: string) =>
      [...queryKeys.fundReports.details(), fundId, reportId] as const,
    definitions: (fundId: string) =>
      [...queryKeys.fundReports.all, 'definitions', fundId] as const,
    due: (fundId: string) =>
      [...queryKeys.fundReports.all, 'due', fundId] as const,
    templates: (fundId: string) =>
      [...queryKeys.fundReports.all, 'templates', fundId] as const,
    template: (fundId: string, templateId: string) =>
      [...queryKeys.fundReports.all, 'template', fundId, templateId] as const,
    fields: (fundId: string, definitionId: string) =>
      [...queryKeys.fundReports.all, 'fields', fundId, definitionId] as const,
  },

  metrics: {
    all: ['metrics'] as const,
    dashboard: () => [...queryKeys.metrics.all, 'dashboard'] as const,
    fund: (fundId: string, view: LedgerView, asOf?: string) =>
      [...queryKeys.metrics.all, 'fund', fundId, view, asOf] as const,
    portfolio: (portfolioId?: string) =>
      [...queryKeys.metrics.all, 'portfolio', portfolioId] as const,
  },

  // Pending Tasks
  pendingTasks: {
    all: ['pendingTasks'] as const,
    lists: () => [...queryKeys.pendingTasks.all, 'list'] as const,
    list: (query: PendingTasksQuery) =>
      [...queryKeys.pendingTasks.lists(), query] as const,
    status: () => [...queryKeys.pendingTasks.all, 'status'] as const,
  },

  // News
  news: {
    all: ['news'] as const,
    sources: () => [...queryKeys.news.all, 'sources'] as const,
    articles: (filters?: Record<string, unknown>) =>
      [...queryKeys.news.all, 'articles', filters] as const,
  },

  // Fund Costs
  fundCosts: {
    all: ['fundCosts'] as const,
    list: (fundId: string, params?: Record<string, unknown>) =>
      [
        ...queryKeys.fundCosts.all,
        'list',
        fundId,
        ...(params ? [params] : []),
      ] as const,
    detail: (fundId: string, costId: string) =>
      [...queryKeys.fundCosts.all, 'detail', fundId, costId] as const,
    summary: (fundId: string) =>
      [...queryKeys.fundCosts.all, 'summary', fundId] as const,
    recognition: (fundId: string, costId: string) =>
      [...queryKeys.fundCosts.all, 'recognition', fundId, costId] as const,
  },

  // Deal Costs
  dealCosts: {
    all: ['dealCosts'] as const,
    list: (fundId: string, params?: Record<string, unknown>) =>
      [
        ...queryKeys.dealCosts.all,
        'list',
        fundId,
        ...(params ? [params] : []),
      ] as const,
    detail: (fundId: string, costId: string) =>
      [...queryKeys.dealCosts.all, 'detail', fundId, costId] as const,
    summary: (fundId: string) =>
      [...queryKeys.dealCosts.all, 'summary', fundId] as const,
    investments: (fundId: string) =>
      [...queryKeys.dealCosts.all, 'investments', fundId] as const,
  },

  // FX Rates
  fxRates: {
    all: ['fxRates'] as const,
    currencies: () => [...queryKeys.fxRates.all, 'currencies'] as const,
    list: (params?: Record<string, unknown>) =>
      [...queryKeys.fxRates.all, 'list', ...(params ? [params] : [])] as const,
    latest: (params?: Record<string, unknown>) =>
      [
        ...queryKeys.fxRates.all,
        'latest',
        ...(params ? [params] : []),
      ] as const,
    history: (params?: Record<string, unknown>) =>
      [
        ...queryKeys.fxRates.all,
        'history',
        ...(params ? [params] : []),
      ] as const,
  },

  // Ponto Connect (bank feed) admin
  ponto: {
    all: ['ponto'] as const,
    accounts: () => [...queryKeys.ponto.all, 'accounts'] as const,
  },

  // Fee Accruals
  feeAccruals: {
    all: ['feeAccruals'] as const,
    list: (fundId: string, granularity?: string) =>
      [...queryKeys.feeAccruals.all, 'list', fundId, granularity] as const,
  },

  // Management Fees
  managementFees: {
    all: ['managementFees'] as const,
    list: (fundId: string, params?: Record<string, unknown>) =>
      [
        ...queryKeys.managementFees.all,
        'list',
        fundId,
        ...(params ? [params] : []),
      ] as const,
    detail: (fundId: string, scheduleId: string) =>
      [...queryKeys.managementFees.all, 'detail', fundId, scheduleId] as const,
    summary: (fundId: string) =>
      [...queryKeys.managementFees.all, 'summary', fundId] as const,
  },

  // Financing Rounds
  rounds: {
    all: ['rounds'] as const,
    list: (companyId: string) => ['rounds', 'company', companyId] as const,
  },

  // Cap Table
  capTable: {
    all: ['capTable'] as const,
    company: (companyId: string) =>
      [...queryKeys.capTable.all, 'company', companyId] as const,
    scenarios: (companyId: string) =>
      [...queryKeys.capTable.all, 'company', companyId, 'scenarios'] as const,
    scenario: (scenarioId: string) =>
      [...queryKeys.capTable.all, 'scenario', scenarioId] as const,
    waterfall: (companyId: string) =>
      [...queryKeys.capTable.all, 'company', companyId, 'waterfall'] as const,
    convertibles: (companyId: string) =>
      [
        ...queryKeys.capTable.all,
        'company',
        companyId,
        'convertibles',
      ] as const,
    maturityPreview: (
      companyId: string,
      instrumentId: string,
      price?: number
    ) =>
      [
        ...queryKeys.capTable.all,
        'company',
        companyId,
        'maturity-preview',
        instrumentId,
        price,
      ] as const,
    resolutionDocuments: (instrumentId: string) =>
      [
        ...queryKeys.capTable.all,
        'resolution-documents',
        instrumentId,
      ] as const,
    transactionDocuments: (companyId: string, transactionId: string) =>
      [
        ...queryKeys.capTable.all,
        'company',
        companyId,
        'transactions',
        transactionId,
        'documents',
      ] as const,
    transactionReconciliation: (companyId: string, transactionId: string) =>
      [
        ...queryKeys.capTable.all,
        'company',
        companyId,
        'transactions',
        transactionId,
        'reconciliation',
      ] as const,
    transactionActivity: (companyId: string, transactionId: string) =>
      [
        ...queryKeys.capTable.all,
        'company',
        companyId,
        'transactions',
        transactionId,
        'activity',
      ] as const,
    transactionBankEntries: (companyId: string, transactionId: string) =>
      [
        ...queryKeys.capTable.all,
        'company',
        companyId,
        'transactions',
        transactionId,
        'bank-entries',
      ] as const,
    transactionFundTransactions: (companyId: string, transactionId: string) =>
      [
        ...queryKeys.capTable.all,
        'company',
        companyId,
        'transactions',
        transactionId,
        'fund-transactions',
      ] as const,
    roundEdit: (companyId: string, roundId: string) =>
      [
        ...queryKeys.capTable.all,
        'company',
        companyId,
        'rounds',
        roundId,
        'edit',
      ] as const,
    splitPreview: (
      companyId: string,
      params: {
        transaction_date: string;
        // Optional, matching PreviewStockSplitRequest: omitting it is a
        // whole-company split, the ordinary shape.
        share_class_id?: string;
        ratio_numerator: string;
        ratio_denominator: string;
        transaction_id?: string;
      }
    ) =>
      [
        ...queryKeys.capTable.all,
        'company',
        companyId,
        'split-preview',
        params,
      ] as const,
  },

  // Cap Table (alias for compatibility)
  captable: {
    all: ['capTable'] as const,
    company: (companyId: string) =>
      [...queryKeys.capTable.all, 'company', companyId] as const,
    scenarios: (companyId: string) =>
      [...queryKeys.capTable.all, 'company', companyId, 'scenarios'] as const,
    scenario: (scenarioId: string) =>
      [...queryKeys.capTable.all, 'scenario', scenarioId] as const,
    waterfall: (companyId: string) =>
      [...queryKeys.capTable.all, 'company', companyId, 'waterfall'] as const,
    convertibles: (companyId: string) =>
      [
        ...queryKeys.capTable.all,
        'company',
        companyId,
        'convertibles',
      ] as const,
    maturityPreview: (
      companyId: string,
      instrumentId: string,
      price?: number
    ) =>
      [
        ...queryKeys.capTable.all,
        'company',
        companyId,
        'maturity-preview',
        instrumentId,
        price,
      ] as const,
    resolutionDocuments: (instrumentId: string) =>
      [
        ...queryKeys.capTable.all,
        'resolution-documents',
        instrumentId,
      ] as const,
  },

  // Capital Accounts
  capitalAccounts: {
    all: ['capitalAccounts'] as const,
    fund: (fundId: string) =>
      [...queryKeys.capitalAccounts.all, 'fund', fundId] as const,
  },

  // Drawdowns
  drawdowns: {
    all: ['drawdowns'] as const,
    lists: () => [...queryKeys.drawdowns.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.drawdowns.lists(), filters] as const,
    details: () => [...queryKeys.drawdowns.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.drawdowns.details(), id] as const,
    statistics: () => [...queryKeys.drawdowns.all, 'statistics'] as const,
    matrix: (fundId: string) =>
      [...queryKeys.drawdowns.all, 'matrix', fundId] as const,
    overduePayments: () =>
      [...queryKeys.drawdowns.all, 'overdue-payments'] as const,
  },

  // Prepayment Pool
  prepaymentPool: {
    all: ['prepaymentPool'] as const,
    summary: (fundId: string) =>
      [...queryKeys.prepaymentPool.all, 'summary', fundId] as const,
    balances: (fundId: string) =>
      [...queryKeys.prepaymentPool.all, 'balances', fundId] as const,
    investor: (fundId: string, investorId: string) =>
      [
        ...queryKeys.prepaymentPool.all,
        'investor',
        fundId,
        investorId,
      ] as const,
    statistics: (fundId: string) =>
      [...queryKeys.prepaymentPool.all, 'statistics', fundId] as const,
  },

  // Templates
  templates: {
    all: ['templates'] as const,
    documents: () => [...queryKeys.templates.all, 'documents'] as const,
    document: (id: string) =>
      [...queryKeys.templates.all, 'document', id] as const,
    documentPreviewHtml: (id: string) =>
      [...queryKeys.templates.all, 'document', id, 'preview-html'] as const,
    emails: () => [...queryKeys.templates.all, 'emails'] as const,
    email: (id: string) => [...queryKeys.templates.all, 'email', id] as const,
  },

  // LPA Types (Lookup Tables)
  lpaTypes: {
    all: ['lpaTypes'] as const,
    feeBasisTypes: (activeOnly?: boolean) =>
      [...queryKeys.lpaTypes.all, 'feeBasisTypes', activeOnly] as const,
    waterfallTypes: (activeOnly?: boolean) =>
      [...queryKeys.lpaTypes.all, 'waterfallTypes', activeOnly] as const,
  },

  // Portal Roles & Modules (Investor Portal RBAC)
  portalRoles: {
    all: ['portalRoles'] as const,
    lists: () => [...queryKeys.portalRoles.all, 'list'] as const,
    list: (params?: { include_system?: boolean; include_inactive?: boolean }) =>
      [...queryKeys.portalRoles.lists(), params] as const,
    details: () => [...queryKeys.portalRoles.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.portalRoles.details(), id] as const,
  },

  portalModules: {
    all: ['portalModules'] as const,
    lists: () => [...queryKeys.portalModules.all, 'list'] as const,
    list: (params?: { include_inactive?: boolean }) =>
      [...queryKeys.portalModules.lists(), params] as const,
    details: () => [...queryKeys.portalModules.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.portalModules.details(), id] as const,
  },
  // Fund Transactions
  fundTransactions: {
    all: ['fundTransactions'] as const,
    lists: (fundId: string) => ['fundTransactions', fundId, 'list'] as const,
    list: (
      fundId: string,
      params?: {
        transaction_type?: FundTransactionType;
        transaction_category?: FundTransactionCategory;
        status?: FundTransactionStatus;
        date_from?: string;
        date_to?: string;
        sort_order?: 'asc' | 'desc';
        page?: number;
        page_size?: number;
      }
    ) => ['fundTransactions', fundId, 'list', params ?? null] as const,
    detail: (fundId: string, id: string) =>
      ['fundTransactions', fundId, 'detail', id] as const,
    // Under detail: every write that refreshes the transaction refreshes its log.
    activity: (fundId: string, id: string) =>
      ['fundTransactions', fundId, 'detail', id, 'activity'] as const,
    reconciliation: (fundId: string, id: string) =>
      ['fundTransactions', fundId, 'reconciliation', id] as const,
    bankEntries: (fundId: string, id: string) =>
      ['fundTransactions', fundId, 'bankEntries', id] as const,
    journalEntries: (fundId: string, id: string) =>
      ['fundTransactions', fundId, 'journalEntries', id] as const,
    bankMatching: (fundId: string, id: string) =>
      ['fundTransactions', fundId, 'bankMatching', id] as const,
    documents: (fundId: string, id: string) =>
      ['fundTransactions', fundId, 'documents', id] as const,
    // One investment's split moves every other investment's remaining commitment.
    lpAllocationsAll: (fundId: string) =>
      ['fundTransactions', fundId, 'lpAllocations'] as const,
    lpAllocations: (fundId: string, id: string) =>
      ['fundTransactions', fundId, 'lpAllocations', id] as const,
  },

  // Fund Members (Carry Portal)
  fundMembers: {
    all: ['fundMembers'] as const,
    lists: () => [...queryKeys.fundMembers.all, 'list'] as const,
    list: (pagination: { page: number; page_size: number }) =>
      [...queryKeys.fundMembers.lists(), pagination] as const,
    details: () => [...queryKeys.fundMembers.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.fundMembers.details(), id] as const,
    funds: (id: string) => [...queryKeys.fundMembers.all, 'funds', id] as const,
    options: () => [...queryKeys.fundMembers.all, 'options'] as const,
  },

  // Lists are the per-fund overview; a detail is one fund's documents.
  carryDocuments: {
    all: ['carryDocuments'] as const,
    lists: () => [...queryKeys.carryDocuments.all, 'list'] as const,
    details: () => [...queryKeys.carryDocuments.all, 'detail'] as const,
    detail: (fundId: string) =>
      [...queryKeys.carryDocuments.details(), fundId] as const,
  },

  carryFunds: {
    all: ['carryFunds'] as const,
    lists: () => [...queryKeys.carryFunds.all, 'list'] as const,
    details: () => [...queryKeys.carryFunds.all, 'detail'] as const,
    detail: (fundId: string) =>
      [...queryKeys.carryFunds.details(), fundId] as const,
    portfolio: (fundId: string) =>
      [...queryKeys.carryFunds.detail(fundId), 'portfolio'] as const,
    ledger: (fundId: string) =>
      [...queryKeys.carryFunds.detail(fundId), 'ledger'] as const,
    stak: () => [...queryKeys.carryFunds.all, 'stak'] as const,
    cooperative: () => [...queryKeys.carryFunds.all, 'cooperative'] as const,
    structure: () => [...queryKeys.carryFunds.all, 'structure'] as const,
    holdingCompany: (fundMemberId: string) =>
      [...queryKeys.carryFunds.structure(), 'holding', fundMemberId] as const,
  },

  // Apart from carryFunds: refreshing a fund after a freeze must not ask for
  // the preview of a holder who is now frozen.
  carryFreezePreview: (fundId: string, fundMemberId: string) =>
    ['carryFreezePreview', fundId, fundMemberId] as const,

  carryDocumentTypes: {
    all: ['carryDocumentTypes'] as const,
    lists: () => [...queryKeys.carryDocumentTypes.all, 'list'] as const,
  },

  // Asset Transactions
  assetTransactions: {
    all: ['assetTransactions'] as const,
    list: (companyId: string) =>
      ['assetTransactions', 'company', companyId] as const,
    detail: (id: string) => ['assetTransactions', 'detail', id] as const,
  },
} as const;
